import React, {
  useRef,
  useState,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
} from 'react';
import {
  useResizeObserver,
  useMutationObserver,
} from '@fortress-technology-solutions/fortress-component-library/hooks';
import { styled } from '@fortress-technology-solutions/fortress-component-library/utils';
import {
  APP_BAR_HEIGHT,
  PADDING,
  MUTATION_OBSERVER_OPTIONS,
} from './constants';
import { debounce } from 'lodash';
import { calculateChildrenHeight } from './utils';

const Container = styled('div')(({ fullPage }) => ({
  height: fullPage ? '100%' : 'auto',
}));

/**
 * TableLayout Component
 *
 * A layout wrapper that dynamically calculates the available height for a table (or other content)
 * based on the window size and other elements on the page. It ensures the content fits within
 * the viewport or parent container without unnecessary scrolling of the entire page.
 *
 * @component
 *
 * @param {Object} props
 * @param {Function} props.children - Render prop that receives the calculated `tableHeight`.
 *                                    Example: `(tableHeight) => <Table height={tableHeight} ... />`
 * @param {string} props.name - Unique identifier for the component instance. Used for the container ID.
 * @param {string[]} [props.watch] - Array of element IDs to watch for resize events.
 *                                   If these elements change size, the table height is recalculated.
 * @param {Object} [props.sx] - MUI system props or style object to extend the container styles.
 * @param {number} [props.childrenElementsHeight=0] - Height of other elements inside the TableLayout that should be
 * subtracted from the available table height.
 * @param {number} [props.containerElementsHeight=0] - Height of elements outside the TableLayout (like headers) that
 * should be subtracted from the container height.
 * @param {boolean} [props.fullPage=false] - If true, the table will take up the remaining height of the page.
 *
 * @returns {JSX.Element} The TableLayout container.
 *
 * @example
 * <TableLayout
 *   name="my-table"
 *   watch={['filter-bar']}
 *   childrenElementsHeight={50}
 * >
 *   {(tableHeight) => (
 *     <MyTable height={tableHeight} data={data} />
 *   )}
 * </TableLayout>
 */
const TableLayout = ({
  children,
  name,
  watch,
  sx,
  childrenElementsHeight = 0,
  containerElementsHeight = 0,
  fullPage = false,
}) => {
  const ref = useRef(null);

  const [containerHeight, setContainerHeight] = useState(
    window.innerHeight - (APP_BAR_HEIGHT + containerElementsHeight),
  );
  const [tableHeight, setTableHeight] = useState(
    containerHeight - (childrenElementsHeight + PADDING),
  );

  const calculateDimensions = useMemo(
    () =>
      debounce(() => {
        if (!ref.current) return;

        let availableHeight;

        if (fullPage) {
          availableHeight = window.innerHeight - APP_BAR_HEIGHT;
        } else {
          // Use the parent's height or a fixed calculation if not fullPage
          // This logic might need adjustment based on how non-fullPage usage is intended
          // For now, preserving existing logic which seems to rely on window height minus offsets
          // or potentially just the container's own height if it was set externally.
          // However, the original code used window.innerHeight - rect.top effectively.
          const rect = ref.current.getBoundingClientRect();
          availableHeight =
            window.innerHeight - rect.top - containerElementsHeight;
        }

        // 2. Update container height state
        if (!isNaN(availableHeight) && availableHeight > 0) {
          setContainerHeight(availableHeight);
        }

        // 3. Calculate table height
        const calculatedTableHeight = calculateChildrenHeight({
          children: ref.current.children,
          containerHeight: availableHeight,
          childrenElementsHeight,
        });

        if (!isNaN(calculatedTableHeight) && calculatedTableHeight > 0) {
          setTableHeight(calculatedTableHeight);
        }
      }, 100),
    [containerElementsHeight, childrenElementsHeight, fullPage],
  );

  useEffect(() => {
    return () => {
      calculateDimensions.cancel();
    };
  }, [calculateDimensions]);

  // Recalculate page height if the container elements height prop changes
  useEffect(() => {
    calculateDimensions();
  }, [containerElementsHeight, calculateDimensions]);

  // mutation observer to watch for changes in children
  useMutationObserver(
    ref,
    useCallback(
      (mutationsList, observer) => {
        // If the target node or its children's attributes change, do something
        for (let mutation of mutationsList) {
          if (mutation.type === 'childList') {
            if (!ref.current) return;
            calculateDimensions();
          } else if (mutation.type === 'attributes') {
            if (!ref.current) return;
            calculateDimensions();
          }
        }
      },
      [calculateDimensions],
    ),
    MUTATION_OBSERVER_OPTIONS,
  );

  useResizeObserver(
    document.body,
    useCallback(
      (entry) => {
        calculateDimensions();
      },
      [calculateDimensions],
    ),
  );

  useEffect(() => {
    if (!ref.current) return;
    calculateDimensions();
  }, [calculateDimensions]);

  useLayoutEffect(() => {
    if (!ref.current?.children) return;

    // resize observe the children that match the watch ids
    const children = ref.current.children;
    const resizeObserver = new ResizeObserver(() => {
      if (!ref.current) return;
      calculateDimensions();
    });

    // Also watch for elements inside children that match the watch ids
    if (watch) {
      watch.forEach((id) => {
        const element = document.getElementById(id);
        if (element) {
          resizeObserver.observe(element);
        }
      });
    }

    Array.from(children).forEach((child) => {
      if (watch?.includes(child.id)) {
        resizeObserver.observe(child);
      }
    });

    return () => resizeObserver.disconnect();
  }, [calculateDimensions, watch]);

  return (
    <Container
      ref={ref}
      id={`table-layout_${name}`}
      fullPage={fullPage}
      sx={{
        overflow: 'hidden',
        height: containerHeight,
        padding: 2,
        ...sx,
      }}
    >
      {children(tableHeight)}
    </Container>
  );
};

export default TableLayout;
